// src/pages/Parties.jsx
import { useState, useEffect } from 'react';
import { api } from '../api';
import { useApp } from '../AppContext';
import { Card, PageHeader, Table, TR, TD, Badge, Btn, Modal, Flash, Spinner, Empty, Field, Input, Select } from '../components/UI';

function PartyForm({ party, onSave, onClose, pricing }) {
  const isNew = !party;
  const [form, setForm] = useState({
    party_date: party?.party_date || new Date().toISOString().slice(0, 10),
    membership_number: party?.membership_number || '',
    lastname: party?.lastname || '',
    payer: party?.payer || 'member',
    charcoal_bags: party?.charcoal_bags || 0,
    payment_method: party?.payment_method || 'cash',
    guard_name: party?.guard_name || '',
    notes: party?.notes || '',
  });
  const [guests, setGuests] = useState(party?.guests || []);
  const [saving, setSaving] = useState(false);

  function addGuest() {
    setGuests(prev => [...prev, { lastname: '', firstnames: '', swimmers: 0, non_swimmers: 0, under_2: 0, payment_method: 'cash' }]);
  }
  function updateGuest(i, field, val) {
    setGuests(prev => prev.map((g, idx) => idx === i ? { ...g, [field]: val } : g));
  }
  function removeGuest(i) {
    setGuests(prev => prev.filter((_, idx) => idx !== i));
  }

  function calcTotal(guestList, topMethod, charcoalBags, payer) {
    const swimCash  = pricing.party_swim_cash    || 5.00;
    const swimCard  = pricing.party_swim_card    || 5.25;
    const nonCash   = pricing.party_nonswim_cash || 2.00;
    const nonCard   = pricing.party_nonswim_card || 2.12;
    const venueRate = pricing.party_venue        || 25.00;
    const coalRate  = pricing.charcoal           || 20.00;

    let swimCost = 0, nonCost = 0, headcount = 0;
    for (const g of guestList) {
      const m = payer === 'member' ? topMethod : g.payment_method; // When member pays, top-level method applies to all; when guests pay, each guest's own method is authoritative
      swimCost += g.swimmers    * (m === 'card' ? swimCard : swimCash);
      nonCost  += g.non_swimmers * (m === 'card' ? nonCard  : nonCash);
      headcount += (g.swimmers || 0) + (g.non_swimmers || 0) + (g.under_2 || 0);
    }
    const cardSurcharge = (amount) => topMethod === 'card' ? Math.floor(amount / 5) * 0.25 : 0;
    const venueFeeBase = headcount > 0 ? Math.ceil(headcount / 25) * venueRate : 0;
    const venueFee = venueFeeBase + cardSurcharge(venueFeeBase);
    const coalCostBase = (charcoalBags || 0) * coalRate;
    const coalCost = coalCostBase + cardSurcharge(coalCostBase);
    return swimCost + nonCost + venueFee + coalCost;
  }

  const liveTotal = calcTotal(guests, form.payment_method, form.charcoal_bags, form.payer);

  async function save() {
    setSaving(true);
    const r = await api.post('/api/party/save', { ...form, id: party?.id, guests });
    setSaving(false);
    if (r?.ok) onSave(r.data);
  }

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
        <Field label="Date"><Input type="date" value={form.party_date} onChange={e => setForm(f => ({ ...f, party_date: e.target.value }))} /></Field>
        <Field label="Last Name"><Input value={form.lastname} onChange={e => setForm(f => ({ ...f, lastname: e.target.value }))} /></Field>
        <Field label="Membership #"><Input value={form.membership_number} onChange={e => setForm(f => ({ ...f, membership_number: e.target.value }))} /></Field>
        <Field label="Who Pays">
          <Select value={form.payer} onChange={e => setForm(f => ({ ...f, payer: e.target.value }))}>
            <option value="member">Member</option>
            <option value="guests">Guests</option>
          </Select>
        </Field>
        <Field label="Charcoal Bags"><Input type="number" min={0} value={form.charcoal_bags} onChange={e => setForm(f => ({ ...f, charcoal_bags: parseInt(e.target.value) || 0 }))} /></Field>
        <Field label="Payment Method">
          <Select value={form.payment_method} onChange={e => setForm(f => ({ ...f, payment_method: e.target.value }))}>
            <option value="cash">Cash</option>
            <option value="card">Card</option>
          </Select>
        </Field>
        <Field label="Guard Name"><Input value={form.guard_name} onChange={e => setForm(f => ({ ...f, guard_name: e.target.value }))} /></Field>
      </div>
      <Field label="Notes"><Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></Field>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, marginTop: 16 }}>
        <span style={{ fontSize: 12, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--fg2)' }}>Guest Groups ({guests.length})</span>
        <Btn variant="outline" style={{ fontSize: 11 }} onClick={addGuest}>+ Add Group</Btn>
      </div>

      {guests.map((g, i) => (
        <div key={i} style={{ border: '1px solid var(--border)', padding: 12, marginBottom: 8, background: 'var(--bg2)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto auto auto 1fr auto', gap: 8, alignItems: 'flex-end' }}>
            <Field label="Last Name" style={{ margin: 0 }}><Input value={g.lastname} onChange={e => updateGuest(i, 'lastname', e.target.value)} /></Field>
            <Field label="First Names" style={{ margin: 0 }}><Input value={g.firstnames} onChange={e => updateGuest(i, 'firstnames', e.target.value)} /></Field>
            <Field label="Swimmers" style={{ margin: 0 }}><Input type="number" min={0} value={g.swimmers} onChange={e => updateGuest(i, 'swimmers', parseInt(e.target.value) || 0)} style={{ width: 70 }} /></Field>
            <Field label="Non-swim" style={{ margin: 0 }}><Input type="number" min={0} value={g.non_swimmers} onChange={e => updateGuest(i, 'non_swimmers', parseInt(e.target.value) || 0)} style={{ width: 70 }} /></Field>
            <Field label="Under 2" style={{ margin: 0 }}><Input type="number" min={0} value={g.under_2} onChange={e => updateGuest(i, 'under_2', parseInt(e.target.value) || 0)} style={{ width: 70 }} /></Field>
            <Field label="Method" style={{ margin: 0 }}>
              <Select value={g.payment_method} onChange={e => updateGuest(i, 'payment_method', e.target.value)}>
                <option value="cash">Cash</option><option value="card">Card</option>
              </Select>
            </Field>
            <button onClick={() => removeGuest(i)} style={{ background: 'none', border: 'none', color: '#c00', cursor: 'pointer', fontSize: 16, alignSelf: 'flex-end', paddingBottom: 8 }}>x</button>
          </div>
        </div>
      ))}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, gap: 12, flexWrap: 'wrap' }}>
        <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', padding: '10px 16px', display: 'flex', gap: 20, alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 10, color: 'var(--fg3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>Estimated Total</div>
            <div style={{ fontSize: 22, fontWeight: 'bold', color: 'var(--fg)' }}>${liveTotal.toFixed(2)}</div>
          </div>
          {guests.length > 0 && (
            <div style={{ fontSize: 11, color: 'var(--fg3)', lineHeight: 1.7 }}>
              {(() => {
                const swimCash  = pricing.party_swim_cash    || 5.00;
                const swimCard  = pricing.party_swim_card    || 5.25;
                const nonCash   = pricing.party_nonswim_cash || 2.00;
                const nonCard   = pricing.party_nonswim_card || 2.12;
                const venueRate = pricing.party_venue        || 25.00;
                const coalRate  = pricing.charcoal           || 20.00;
                let swimCost = 0, nonCost = 0, headcount = 0;
                for (const g of guests) {
                  const m = form.payer === 'member' ? form.payment_method : g.payment_method;
                  swimCost  += g.swimmers     * (m === 'card' ? swimCard : swimCash);
                  nonCost   += g.non_swimmers * (m === 'card' ? nonCard  : nonCash);
                  headcount += (g.swimmers || 0) + (g.non_swimmers || 0) + (g.under_2 || 0);
                }
                const cardSurcharge = (amount) => form.payment_method === 'card' ? Math.floor(amount / 5) * 0.25 : 0;
                const venueFeeBase = headcount > 0 ? Math.ceil(headcount / 25) * venueRate : 0;
                const venueFee = venueFeeBase + cardSurcharge(venueFeeBase);
                const coalCostBase = (form.charcoal_bags || 0) * coalRate;
                const coalCost = coalCostBase + cardSurcharge(coalCostBase);
                const lines = [];
                if (swimCost  > 0) lines.push(`Swimmers: $${swimCost.toFixed(2)}`);
                if (nonCost   > 0) lines.push(`Non-swim: $${nonCost.toFixed(2)}`);
                if (venueFee  > 0) lines.push(`Venue (${headcount} guests): $${venueFee.toFixed(2)}`);
                if (coalCost  > 0) lines.push(`Charcoal (${form.charcoal_bags} bag${form.charcoal_bags !== 1 ? 's' : ''}): $${coalCost.toFixed(2)}`);
                return lines.map((l, i) => <div key={i}>{l}</div>);
              })()}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn onClick={save} disabled={saving}>{saving ? 'Saving...' : isNew ? 'Create Party' : 'Save Changes'}</Btn>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        </div>
      </div>
    </div>
  );
}

function CheckIn({ partyId, onClose }) {
  const [party, setParty] = useState(null);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/api/party/${partyId}/guests`).then(r => {
      if (r?.ok) { setParty(r.data.party); setGuests(r.data.guests); }
      setLoading(false);
    });
  }, [partyId]);

  async function markArrived(guestId, arrived, payMethod) {
    const r = await api.post(`/api/party/guest/${guestId}/arrived`, { arrived, payment_method: payMethod });
    if (r?.ok) setGuests(prev => prev.map(g => g.id === guestId ? { ...g, arrived: arrived ? 1 : 0 } : g));
  }

  const arrivedCount = guests.filter(g => g.arrived).length;

  if (loading) return <div style={{ padding: 20, color: 'var(--fg3)' }}>Loading...</div>;

  return (
    <div>
      <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', padding: '10px 14px', marginBottom: 16, display: 'flex', gap: 20, fontSize: 13 }}>
        <span><strong>{party?.lastname}</strong> — {party?.party_date}</span>
        <span style={{ color: 'var(--fg3)' }}>{arrivedCount} / {guests.length} groups arrived</span>
      </div>
      {guests.map(g => (
        <div key={g.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', border: '1px solid var(--border)', marginBottom: 6, background: g.arrived ? 'color-mix(in srgb, var(--success) 8%, var(--card-bg))' : 'var(--card-bg)' }}>
          <div>
            <div style={{ fontWeight: 'bold', fontSize: 13 }}>{g.lastname} {g.firstnames && `— ${g.firstnames}`}</div>
            <div style={{ fontSize: 11, color: 'var(--fg3)' }}>{g.swimmers} swimmers · {g.non_swimmers} non-swim · {g.under_2} under 2</div>
          </div>
          {g.arrived
            ? <Badge variant="green">Arrived</Badge>
            : <Btn variant="outline" style={{ fontSize: 11 }} onClick={() => markArrived(g.id, true, g.payment_method)}>Mark Arrived</Btn>
          }
        </div>
      ))}
      <Btn variant="ghost" style={{ marginTop: 12 }} onClick={onClose}>Close</Btn>
    </div>
  );
}

export default function Parties() {
  const { site } = useApp();
  const pricing = site?.pricing || {};
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editParty, setEditParty] = useState(null);
  const [checkInId, setCheckInId] = useState(null);
  const [flash, setFlash] = useState(null);

  function load() {
    setLoading(true);
    api.get('/api/parties').then(r => { if (r?.ok) setParties(r.data.parties || []); setLoading(false); });
  }

  useEffect(() => { load(); }, []);

  async function closeParty(id) {
    await api.post(`/api/party/${id}/close`, {});
    setParties(prev => prev.map(p => p.id === id ? { ...p, is_closed: 1 } : p));
  }
  async function reopenParty(id) {
    await api.post(`/api/party/${id}/reopen`, {});
    setParties(prev => prev.map(p => p.id === id ? { ...p, is_closed: 0 } : p));
  }
  async function deleteParty(id) {
    if (!window.confirm('Delete this party booking?')) return;
    await api.post(`/api/party/${id}/delete`, {});
    setParties(prev => prev.filter(p => p.id !== id));
  }

  function onSaved() { setShowForm(false); setEditParty(null); setFlash({ type: 'success', msg: 'Party saved.' }); load(); }

  return (
    <div style={{ padding: '20px 16px', maxWidth: 1100, margin: '0 auto' }}>
      <PageHeader title="Party Bookings" actions={<Btn onClick={() => { setEditParty(null); setShowForm(true); }}>New Party</Btn>} />
      {flash && <Flash type={flash.type} onDismiss={() => setFlash(null)}>{flash.msg}</Flash>}

      <Card>
        {loading ? <Spinner /> : parties.length === 0 ? <Empty message="No party bookings yet." /> : (
          <Table headers={['Date', 'Last Name', 'MBR #', 'Payer', 'Guests', 'Total', 'Status', 'Actions']}>
            {parties.map(p => (
              <TR key={p.id}>
                <TD>{p.party_date}</TD>
                <TD style={{ fontWeight: 'bold', color: 'var(--fg)' }}>{p.lastname}</TD>
                <TD>{p.membership_number || '—'}</TD>
                <TD>{p.payer}</TD>
                <TD>{p.charcoal_bags ? `${p.charcoal_bags} bag${p.charcoal_bags !== 1 ? 's' : ''}` : '—'}</TD>
                <TD style={{ fontWeight: 'bold', color: 'var(--fg)' }}>${(p.total_charge || 0).toFixed(2)}</TD>
                <TD><Badge variant={p.is_closed ? 'gray' : 'green'}>{p.is_closed ? 'Closed' : 'Open'}</Badge></TD>
                <TD>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    <Btn variant="outline" style={{ fontSize: 10, padding: '3px 7px' }} onClick={() => setCheckInId(p.id)}>Check-In</Btn>
                    {!p.is_closed
                      ? <Btn variant="ghost" style={{ fontSize: 10, padding: '3px 7px' }} onClick={() => { setEditParty(p); setShowForm(true); }}>Edit</Btn>
                      : null}
                    {!p.is_closed
                      ? <Btn variant="ghost" style={{ fontSize: 10, padding: '3px 7px' }} onClick={() => closeParty(p.id)}>Close</Btn>
                      : <Btn variant="ghost" style={{ fontSize: 10, padding: '3px 7px' }} onClick={() => reopenParty(p.id)}>Reopen</Btn>}
                    <a href={api.flaskUrl(`/party/${p.id}/pdf`)} style={{ padding: '3px 7px', fontSize: 10, color: 'var(--accent)', border: '1px solid var(--accent)', textDecoration: 'none', fontWeight: 'bold' }}>PDF</a>
                    <Btn variant="danger" style={{ fontSize: 10, padding: '3px 7px' }} onClick={() => deleteParty(p.id)}>Delete</Btn>
                  </div>
                </TD>
              </TR>
            ))}
          </Table>
        )}
      </Card>

      {showForm && (
        <Modal title={editParty ? `Edit Party — ${editParty.lastname}` : 'New Party Booking'} onClose={() => { setShowForm(false); setEditParty(null); }} width={800}>
          <PartyForm party={editParty} onSave={onSaved} onClose={() => { setShowForm(false); setEditParty(null); }} pricing={pricing} />
        </Modal>
      )}

      {checkInId && (
        <Modal title="Party Check-In" onClose={() => setCheckInId(null)} width={600}>
          <CheckIn partyId={checkInId} onClose={() => setCheckInId(null)} />
        </Modal>
      )}
    </div>
  );
}
